/*
 * The one product search used by the header search box and the search
 * palette: debounced `GET /products?search=` suggestions plus the visitor's
 * recent searches (localStorage). Each caller gets its own query/results;
 * recent searches are shared.
 */
import { onBeforeUnmount, ref, watch } from 'vue';
import { api } from '../api/client';
import type { Paged, ProductSummary } from '../types/product';

export const SEARCH_MIN_CHARS = 2;
export const SEARCH_LIMIT = 6;
/** Long enough to skip keystrokes, short enough to feel live. */
const DEBOUNCE_MS = 220;

const RECENT_KEY = 'keyzo.searches.v1';
const RECENT_MAX = 6;

function loadRecent(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

// Module-level so every search box sees the same list.
const recent = ref<string[]>(loadRecent());

function persistRecent() {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.value));
  } catch {
    // convenience only
  }
}

export function rememberSearch(term: string) {
  const clean = term.trim();
  if (clean.length < SEARCH_MIN_CHARS) return;
  recent.value = [clean, ...recent.value.filter((s) => s.toLowerCase() !== clean.toLowerCase())].slice(0, RECENT_MAX);
  persistRecent();
}

export function forgetSearch(term: string) {
  recent.value = recent.value.filter((s) => s !== term);
  persistRecent();
}

/** `limit` lets the full-screen overlay ask for a bento's worth of results. */
export function useProductSearch(limit: number = SEARCH_LIMIT) {
  const query = ref('');
  const results = ref<ProductSummary[]>([]);
  const total = ref(0);
  const loading = ref(false);

  let timer: number | undefined;
  let controller: AbortController | null = null;

  async function fetchNow(term: string) {
    controller?.abort();
    controller = new AbortController();
    loading.value = true;
    try {
      const { data } = await api.get<Paged<ProductSummary>>('/products', {
        params: { search: term, limit, inStockOnly: true },
        signal: controller.signal,
      });
      results.value = data.items;
      total.value = data.total;
    } catch {
      // aborted or failed — keep the previous list rather than flashing empty
    } finally {
      loading.value = false;
    }
  }

  watch(query, (q) => {
    window.clearTimeout(timer);
    const term = q.trim();
    if (term.length < SEARCH_MIN_CHARS) {
      controller?.abort();
      results.value = [];
      total.value = 0;
      loading.value = false;
      return;
    }
    timer = window.setTimeout(() => void fetchNow(term), DEBOUNCE_MS);
  });

  function reset() {
    query.value = '';
    results.value = [];
    total.value = 0;
  }

  onBeforeUnmount(() => {
    window.clearTimeout(timer);
    controller?.abort();
  });

  return { query, results, total, loading, recent, reset, rememberSearch, forgetSearch };
}
